// Mirror of Discord API embed structure (subset used in Phase 1).
export interface DiscordEmbedField {
  name: string;
  value: string;
  inline?: boolean;
}

export interface DiscordEmbedAuthor {
  name: string;
  url?: string;
  icon_url?: string;
}

export interface DiscordEmbedFooter {
  text: string;
  icon_url?: string;
}

export interface DiscordEmbedMedia {
  url: string;
}

export interface DiscordEmbed {
  title?: string;
  description?: string;
  url?: string;
  timestamp?: string; // ISO
  color?: number; // decimal (0..0xFFFFFF)
  author?: DiscordEmbedAuthor;
  footer?: DiscordEmbedFooter;
  image?: DiscordEmbedMedia;
  thumbnail?: DiscordEmbedMedia;
  fields?: DiscordEmbedField[];
}

// UI-friendly form state (color as hex string, timestamp as boolean toggle "now")
export interface EmbedFormState {
  title: string;
  description: string;
  url: string;
  colorHex: string; // "#5865f2" or ""
  withTimestamp: boolean;
  authorName: string;
  authorUrl: string;
  authorIcon: string;
  footerText: string;
  footerIcon: string;
  imageUrl: string;
  thumbnailUrl: string;
  fields: DiscordEmbedField[];
}

export function emptyForm(): EmbedFormState {
  return {
    title: '',
    description: '',
    url: '',
    colorHex: '#5865F2',
    withTimestamp: false,
    authorName: '',
    authorUrl: '',
    authorIcon: '',
    footerText: '',
    footerIcon: '',
    imageUrl: '',
    thumbnailUrl: '',
    fields: [],
  };
}

export function hexToDecimal(hex: string): number | undefined {
  if (!hex) return undefined;
  const cleaned = hex.replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(cleaned)) return undefined;
  return parseInt(cleaned, 16);
}

export function decimalToHex(decimal: number | undefined): string {
  if (decimal === undefined) return '';
  return `#${decimal.toString(16).padStart(6, '0').toUpperCase()}`;
}

export function formToEmbed(form: EmbedFormState): DiscordEmbed {
  const embed: DiscordEmbed = {};
  if (form.title) embed.title = form.title;
  if (form.description) embed.description = form.description;
  if (form.url) embed.url = form.url;
  const color = hexToDecimal(form.colorHex);
  if (color !== undefined) embed.color = color;
  if (form.withTimestamp) embed.timestamp = new Date().toISOString();
  if (form.authorName) {
    embed.author = { name: form.authorName };
    if (form.authorUrl) embed.author.url = form.authorUrl;
    if (form.authorIcon) embed.author.icon_url = form.authorIcon;
  }
  if (form.footerText) {
    embed.footer = { text: form.footerText };
    if (form.footerIcon) embed.footer.icon_url = form.footerIcon;
  }
  if (form.imageUrl) embed.image = { url: form.imageUrl };
  if (form.thumbnailUrl) embed.thumbnail = { url: form.thumbnailUrl };
  if (form.fields.length > 0) embed.fields = form.fields.filter((f) => f.name || f.value);
  return embed;
}

export function embedToForm(embed: DiscordEmbed | null | undefined): EmbedFormState {
  if (!embed) return emptyForm();
  return {
    title: embed.title ?? '',
    description: embed.description ?? '',
    url: embed.url ?? '',
    colorHex: decimalToHex(embed.color) || '#5865F2',
    withTimestamp: Boolean(embed.timestamp),
    authorName: embed.author?.name ?? '',
    authorUrl: embed.author?.url ?? '',
    authorIcon: embed.author?.icon_url ?? '',
    footerText: embed.footer?.text ?? '',
    footerIcon: embed.footer?.icon_url ?? '',
    imageUrl: embed.image?.url ?? '',
    thumbnailUrl: embed.thumbnail?.url ?? '',
    fields: embed.fields ?? [],
  };
}
